import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Timeline, TouchApp, Link as LinkIcon, ShoppingCart } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  fetchAttributionSummary,
  fetchMarketingEvents,
} from '../../../services/marketing';
import CustomPagination from '../../../components/common/CustomPagination.js';

const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    return format(new Date(value), 'MMM dd, yyyy HH:mm');
  } catch {
    return 'N/A';
  }
};

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%', borderTop: `3px solid ${color}` }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color, wordBreak: 'break-word' }}>
            {Number(value || 0).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const MarketingAttribution = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    event_name: '',
    platform: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0,
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const cleanParams = (params) => {
    const cleaned = { ...params };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === '' || cleaned[key] === null || cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const commonFilters = cleanParams({
        date_from: filters.date_from,
        date_to: filters.date_to,
      });
      const eventParams = cleanParams({
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage,
      });
      const [summaryResponse, eventsResponse] = await Promise.all([
        fetchAttributionSummary(commonFilters),
        fetchMarketingEvents(eventParams),
      ]);
      setSummary(summaryResponse);
      setEvents(eventsResponse.results || []);
      setPagination((prev) => ({
        ...prev,
        totalCount: eventsResponse.count || 0,
      }));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch attribution data');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0 && value <= 1000) {
      setPagination((prev) => ({ ...prev, rowsPerPage: value, page: 0 }));
    }
    setCustomRowsInput('');
    setCustomRowsActive(false);
  };

  const rows = useMemo(() => events.map((event, index) => ({
    ...event,
    sn: pagination.page * pagination.rowsPerPage + index + 1,
  })), [events, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    { field: 'sn', headerName: 'S/N', width: 75, sortable: false, filterable: false },
    { field: 'event_name', headerName: 'Event', minWidth: 190, flex: 1, sortable: false },
    {
      field: 'identity_anonymous_id',
      headerName: 'Marketing ID',
      minWidth: 210,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          title={params.value || ''}
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.86rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            userSelect: 'text',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    { field: 'user_display', headerName: 'User', minWidth: 180, flex: 1, sortable: false },
    {
      field: 'platform',
      headerName: 'Platform',
      width: 120,
      sortable: false,
      renderCell: (params) => <Chip size="small" label={params.value || 'unknown'} />,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 130,
      sortable: false,
      renderCell: (params) => <Chip size="small" color="info" variant="outlined" label={params.value || 'backend'} />,
    },
    { field: 'touch_source', headerName: 'Touch Source', minWidth: 150, flex: 1, sortable: false },
    { field: 'touch_campaign', headerName: 'Campaign', minWidth: 170, flex: 1, sortable: false },
    { field: 'order_number', headerName: 'Order', minWidth: 150, flex: 1, sortable: false },
    {
      field: 'value',
      headerName: 'Value',
      width: 135,
      sortable: false,
      renderCell: (params) => params.value ? `${params.row.currency || ''} ${Number(params.value).toLocaleString()}` : 'N/A',
    },
    {
      field: 'occurred_at',
      headerName: 'Occurred At',
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params) => formatDate(params.value),
    },
  ], []);

  const totals = summary?.totals || {};
  const funnel = summary?.funnel || [];
  const topSources = summary?.top_sources || [];
  const orderSources = summary?.orders_by_original_acquisition || [];

  if (loading && !summary && events.length === 0) {
    return (
      <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Attribution
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Events, identities, touchpoints and order attribution
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Events" value={totals.events} icon={<Timeline fontSize="large" />} color={theme.palette.primary.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Touchpoints" value={totals.touchpoints} icon={<TouchApp fontSize="large" />} color={theme.palette.success.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Linked Identities" value={totals.linked_identities} icon={<LinkIcon fontSize="large" />} color={theme.palette.info.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Attributed Orders" value={totals.order_attributions} icon={<ShoppingCart fontSize="large" />} color={theme.palette.warning.main} />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField label="Search" fullWidth value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField label="Event" fullWidth value={filters.event_name} onChange={(e) => handleFilterChange('event_name', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField label="Platform" fullWidth value={filters.platform} onChange={(e) => handleFilterChange('platform', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth value={filters.date_from} onChange={(e) => handleFilterChange('date_from', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth value={filters.date_to} onChange={(e) => handleFilterChange('date_to', e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Funnel Events</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                {funnel.map((row) => (
                  <Box key={row.event_name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2">{row.event_name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{Number(row.count || 0).toLocaleString()}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Top Sources</Typography>
              {topSources.slice(0, 8).map((row, index) => (
                <Box key={`${row.source}-${row.medium}-${row.campaign}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" noWrap>{row.source || 'direct'} / {row.medium || 'none'} / {row.campaign || 'none'}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{Number(row.touches || 0).toLocaleString()}</Typography>
                </Box>
              ))}
              {orderSources.slice(0, 4).map((row, index) => (
                <Box key={`order-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" noWrap>{row.original_acquisition_touch__source || 'unattributed'} orders</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{Number(row.paid_orders || 0).toLocaleString()}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 520 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1250,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.95rem',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={setCustomRowsInput}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default MarketingAttribution;
